// Logs de diagnostico: ativar no console com
//   localStorage.setItem('umabala_debug', '1')  e recarregar a pagina.
// Erros e avisos continuam sempre visiveis.
const UB_DEBUG_GRID = (() => {
  try { return localStorage.getItem('umabala_debug') === '1'; } catch (e) { return false; }
})();

/**
 * Product Grid Matrix Manager
 * Gerencia a grid matricial da página de produto com todas as funcionalidades:
 * - Sincronização com carrinho (Q2)
 * - Click incrementa, long-press zera (Q3)
 * - Variantes sem estoque cinza/traço (Q4)
 * - Indicador visual de estoque máximo (Q5)
 */

class ProductGridMatrix {
  constructor() {
    this.productId = null;
    this.cartData = null;
    this.longPressTimer = null;
    this.longPressDelay = 450; // ms - otimizado para mobile
    this.debounceTimer = null;
    this.debounceDelay = 700; // ms para atualização do carrinho
    this.wasLongPress = false; // Flag para distinguir click de long-press
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.lastTouchTime = 0;
    this.touchDebounceDelay = 150; // ms para evitar toques duplos acidentais
    this.init();
  }
  
  // Função helper para aguardar elemento aparecer no DOM
  async waitForElement(selector, timeout = 5000, interval = 100) {
    const startTime = Date.now();
    while (Date.now() - startTime < timeout) {
      const element = document.querySelector(selector);
      if (element) {
        return element;
      }
      await new Promise(resolve => setTimeout(resolve, interval));
    }
    return null;
  }
  
  // Função helper para garantir que cart-drawer está pronto
  async ensureCartDrawerReady() {
    let cartDrawer = document.querySelector('cart-drawer');
    
    // Se não existe, aguardar aparecer
    if (!cartDrawer) {
      UB_DEBUG_GRID && console.log('⏳ Aguardando cart-drawer aparecer no DOM...');
      cartDrawer = await this.waitForElement('cart-drawer', 3000);
    }
    
    if (!cartDrawer) {
      console.warn('⚠️ cart-drawer não encontrado após timeout');
      return false;
    }
    
    // Verificar se está inicializado
    if (cartDrawer.isInitialized) {
      return true;
    }
    
    // Aguardar até estar inicializado (com timeout)
    const startTime = Date.now();
    const timeout = 2000;
    while (Date.now() - startTime < timeout) {
      if (cartDrawer.isInitialized) {
        UB_DEBUG_GRID && console.log('✅ cart-drawer está pronto!');
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    console.warn('⚠️ cart-drawer não inicializou após timeout');
    return false;
  }
  
  // Função helper para garantir que badge do carrinho existe
  async ensureCartBadgeExists() {
    let cartIconBubble = document.getElementById('cart-icon-bubble');
    
    // Se não existe, aguardar aparecer
    if (!cartIconBubble) {
      UB_DEBUG_GRID && console.log('⏳ Aguardando cart-icon-bubble aparecer no DOM...');
      cartIconBubble = await this.waitForElement('#cart-icon-bubble', 3000);
    }
    
    if (!cartIconBubble) {
      console.warn('⚠️ cart-icon-bubble não encontrado após timeout');
      return null;
    }
    
    // Verificar se o bubble existe dentro do cart-icon-bubble
    let bubble = cartIconBubble.querySelector('.cart-count-bubble');
    if (!bubble) {
      // Criar o bubble dinamicamente se não existir
      UB_DEBUG_GRID && console.log('🔧 Criando cart-count-bubble dinamicamente...');
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      const span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      span.textContent = '0';
      bubble.appendChild(span);
      cartIconBubble.appendChild(bubble);
    }
    
    // Garantir que o span existe dentro do bubble
    let countSpan = bubble.querySelector('span[aria-hidden="true"]') || bubble.querySelector('span');
    if (!countSpan) {
      countSpan = document.createElement('span');
      countSpan.setAttribute('aria-hidden', 'true');
      countSpan.textContent = '0';
      bubble.appendChild(countSpan);
    }
    
    return { cartIconBubble, bubble, countSpan };
  }
  
  // Função helper para feedback háptico
  vibrateDevice(pattern = 50) {
    if ('vibrate' in navigator) {
      try {
        if (Array.isArray(pattern)) {
          navigator.vibrate(pattern);
        } else {
          navigator.vibrate(pattern);
        }
      } catch (e) {
        // Ignorar erros de vibração em navegadores que não suportam
      }
    }
  }
  
  debounce(func, wait) {
    let timeoutId = null;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), wait);
    };
  }
  
  debouncedCartUpdate = null;
  
  // Debounced version of loadCartData to prevent excessive API calls
  debouncedLoadAndSync = null;

  // Cache de swatches para evitar múltiplas requisições
  swatchesCache = {};

  // ===== BUSCAR SWATCH DE COR NATIVO DO SHOPIFY =====
  // Reutiliza a mesma lógica do cart-drawer
  async getColorSwatch(productHandle, colorName) {
    if (!productHandle || !colorName) {
      return this.getColorSwatchFallback(colorName);
    }
    
    // Se já temos os swatches deste produto em cache, usar
    if (this.swatchesCache[productHandle]) {
      const swatches = this.swatchesCache[productHandle];
      const swatch = this.findSwatchInData(swatches, colorName);
      if (swatch && swatch.type) {
        return this.formatSwatchStyle(swatch);
      }
    }
    
    // MÉTODO 1: Buscar via section_id com product_handle na query string
    try {
      const swatchUrl = `/products/${productHandle}?section_id=product-swatches&product_handle=${encodeURIComponent(productHandle)}`;
      const swatchResponse = await fetch(swatchUrl);
      
      if (swatchResponse.ok) {
        const text = await swatchResponse.text();
        const contentType = swatchResponse.headers.get('content-type') || '';
        
        let swatchData = null;
        try {
          swatchData = JSON.parse(text.trim());
        } catch (parseError) {
          // Tentar extrair JSON do HTML
          const jsonMatches = [
            text.match(/\{[\s\S]*?"swatches"[\s\S]*?\}/),
            text.match(/<script[^>]*type=["']application\/json["'][^>]*>([\s\S]*?)<\/script>/i),
            text.match(/<div[^>]*>(\{[\s\S]*?"swatches"[\s\S]*?\})<\/div>/i)
          ];
          
          for (const match of jsonMatches) {
            if (match) {
              try {
                const jsonText = match[1] || match[0];
                swatchData = JSON.parse(jsonText.trim());
                break;
              } catch (e) {
                // Continuar tentando
              }
            }
          }
        }
        
        if (swatchData && swatchData.swatches && !swatchData.error) {
          this.swatchesCache[productHandle] = swatchData.swatches;
          const swatch = this.findSwatchInData(swatchData.swatches, colorName);
          if (swatch && swatch.type) {
            return this.formatSwatchStyle(swatch);
          }
        }
      }
    } catch (error) {
      // Silenciosamente falhar e usar fallback
    }
    
    // Fallback final
    return this.getColorSwatchFallback(colorName);
  }

  // ===== HELPER: Buscar swatch nos dados =====
  findSwatchInData(swatchesData, colorName) {
    if (!swatchesData || !colorName) return null;
    
    const normalizedColorName = String(colorName).trim();
    
    for (const optionName in swatchesData) {
      const optionSwatches = swatchesData[optionName];
      if (!optionSwatches || typeof optionSwatches !== 'object') continue;
      
      // Match exato
      if (optionSwatches[normalizedColorName]) {
        return optionSwatches[normalizedColorName];
      }
      
      // Case-insensitive
      for (const key in optionSwatches) {
        if (key.trim().toLowerCase() === normalizedColorName.toLowerCase()) {
          return optionSwatches[key];
        }
      }
      
      // Match normalizado (remove acentos)
      for (const key in optionSwatches) {
        const normalizedKey = key.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const normalizedSearch = normalizedColorName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (normalizedKey === normalizedSearch) {
          return optionSwatches[key];
        }
      }
    }
    
    return null;
  }

  // ===== HELPER: Formatar estilo do swatch =====
  formatSwatchStyle(swatch) {
    if (!swatch || !swatch.type) {
      return 'background-color: #CCCCCC;';
    }
    
    if (swatch.type === 'image') {
      let style = `background-image: url(${swatch.image}); background-size: cover; background-position: center;`;
      if (swatch.focal_point) {
        style = `background-image: url(${swatch.image}); background-size: cover; background-position: ${swatch.focal_point};`;
      }
      return style;
    } else if (swatch.type === 'color') {
      return `background-color: rgb(${swatch.rgb});`;
    }
    
    return 'background-color: #CCCCCC;';
  }

  // ===== FALLBACK: Lógica antiga =====
  getColorSwatchFallback(colorName) {
    if (!colorName) {
      return 'background-color: #CCCCCC;';
    }
    
    const value = String(colorName).trim();
    if (value.startsWith('#')) {
      return `background-color: ${value};`;
    }
    
    return `background-color: ${value.toLowerCase()};`;
  }

  // ===== APLICAR SWATCHES NAS BOLINHAS DA PÁGINA DE PRODUTO =====
  async applyColorSwatches() {
    const colorSwatches = document.querySelectorAll('.product-color-swatch');
    if (colorSwatches.length === 0) return;
    
    UB_DEBUG_GRID && console.log(`🎨 Aplicando swatches em ${colorSwatches.length} bolinhas de cor...`);
    
    // Agrupar por produto para evitar requisições duplicadas
    const swatchesByProduct = {};
    colorSwatches.forEach(swatch => {
      const productHandle = swatch.dataset.productHandle;
      const colorName = swatch.dataset.colorName;
      
      if (!productHandle || !colorName) return;
      
      if (!swatchesByProduct[productHandle]) {
        swatchesByProduct[productHandle] = [];
      }
      
      swatchesByProduct[productHandle].push({ swatch, colorName });
    });
    
    // Buscar e aplicar swatches para cada produto
    for (const [productHandle, swatches] of Object.entries(swatchesByProduct)) {
      const uniqueColors = [...new Set(swatches.map(s => s.colorName))];
      
      // Buscar swatches para todas as cores únicas deste produto
      const colorSwatchStyles = {};
      for (const colorName of uniqueColors) {
        try {
          const style = await this.getColorSwatch(productHandle, colorName);
          colorSwatchStyles[colorName] = style;
        } catch (error) {
          console.error(`Erro ao buscar swatch para ${colorName}:`, error);
          colorSwatchStyles[colorName] = this.getColorSwatchFallback(colorName);
        }
      }
      
      // Aplicar estilos nas bolinhas
      swatches.forEach(({ swatch, colorName }) => {
        const style = colorSwatchStyles[colorName] || this.getColorSwatchFallback(colorName);
        swatch.style.cssText = swatch.style.cssText.replace(/background-color:[^;]+;?/gi, '');
        swatch.style.cssText += style;
      });
    }
    
    UB_DEBUG_GRID && console.log(`✅ Swatches aplicados com sucesso!`);
  }

  async init() {
    // Aguardar o DOM estar pronto
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  async setup() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;
    
    this.productId = productInfo.dataset.productId;
    if (!this.productId) return;

    // Verificar se usuário está logado
    const gridMatrix = document.querySelector('.variant-grid-matrix');
    const customerData = gridMatrix ? gridMatrix.dataset.customerLoggedIn : null;
    const isLoggedIn = customerData && customerData !== 'false' && customerData !== '';
    
    // Se não estiver logado, não inicializar funcionalidades
    if (!isLoggedIn) {
      UB_DEBUG_GRID && console.log('⚠️ Usuário não logado - funcionalidades de carrinho desabilitadas');
      return;
    }

    // Inicializar função debounced para evitar excesso de API calls
    this.debouncedLoadAndSync = this.debounce(async () => {
      await this.loadCartData();
      this.syncQuantitiesFromCart();
    }, 300);

    // Carregar dados do carrinho para sincronização
    await this.loadCartData();
    
    // Sincronizar quantidades iniciais
    this.syncQuantitiesFromCart();
    
    // Configurar event listeners
    this.setupEventListeners();
    
    // Configurar botão de remover tudo
    this.setupRemoveAllButton();
    
    // Escutar atualizações do carrinho
    this.subscribeToCartUpdates();
    
    // Aplicar swatches nas bolinhas de cor
    await this.applyColorSwatches();
    
    // CRÍTICO: Aguardar cart-drawer estar pronto antes de continuar
    // Isso garante que subscriber está registrado antes de qualquer evento ser disparado
    await this.ensureCartDrawerReady();
    
    // Recarregar e sincronizar quando o carrinho for aberto
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Interceptar o método open do cart drawer
      const originalOpen = cartDrawer.open.bind(cartDrawer);
      cartDrawer.open = async function(...args) {
        await originalOpen(...args);
        // Sincronizar quantidades quando o drawer abrir
        if (productGridMatrixInstance) {
          // O open() do drawer acabou de buscar /cart.js. Reaproveitar essa
          // resposta em vez de disparar a mesma requisicao de novo.
          if (this.lastCartData && Array.isArray(this.lastCartData.items)) {
            productGridMatrixInstance.cartData = this.lastCartData;
          } else {
            await productGridMatrixInstance.loadCartData();
          }
          productGridMatrixInstance.syncQuantitiesFromCart();
        }
      };
    }
    
    UB_DEBUG_GRID && console.log('✅ ProductGridMatrix inicializado e pronto!');
  }

  async loadCartData() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        this.cartData = await response.json();
      }
    } catch (error) {
      console.error('Erro ao carregar dados do carrinho:', error);
    }
  }

  syncQuantitiesFromCart() {
    // CRÍTICO: Verificar se cartData está disponível e válido antes de sincronizar
    if (!this.cartData || !this.cartData.items || !Array.isArray(this.cartData.items)) {
      console.warn('⚠️ syncQuantitiesFromCart: cartData inválido, pulando sincronização');
      return;
    }
    
    const gridInputs = document.querySelectorAll('.quantity-grid-input');
    gridInputs.forEach(input => {
      const variantId = input.dataset.variantId;
      if (!variantId) return;
      
      // Buscar item no carrinho com este variant_id
      const cartItem = this.cartData.items.find(item => 
        item.variant_id && item.variant_id.toString() === variantId.toString()
      );
      
      if (cartItem && cartItem.quantity > 0) {
        // Item está no carrinho - atualizar quantidade
        const newQuantity = cartItem.quantity;
        // Só atualizar se o valor for diferente para evitar atualizações desnecessárias
        if (parseInt(input.value) !== newQuantity) {
          input.value = newQuantity;
          this.updateInputColor(input);
          this.checkStockLimit(input, newQuantity);
        }
      } else {
        // Item NÃO está no carrinho ou quantidade é 0 - zerar input apenas se tiver valor
        const currentValue = parseInt(input.value) || 0;
        if (currentValue > 0) {
          input.value = 0;
          this.updateInputColor(input);
          input.classList.remove('quantity-max-stock', 'quantity-error');
          this.removeMaxStockMessage(input);
        }
      }
    });
  }

  setupEventListeners() {
    const gridInputs = document.querySelectorAll('.quantity-grid-input');
    
    gridInputs.forEach(input => {
      // Remover listeners anteriores se existirem
      const currentValue = input.value; // Salvar valor atual
      const newInput = input.cloneNode(true);
      newInput.value = currentValue; // Restaurar valor
      
      // LIMPAR classes de estado antes de atualizar
      newInput.classList.remove('quantity-zero', 'quantity-clicked', 'quantity-reset', 'quantity-max-stock', 'quantity-error');
      
      input.parentNode.replaceChild(newInput, input);
      
      // Atualizar cor do novo input baseado no valor real
      this.updateInputColor(newInput);
      
      // Flag para distinguir entre desktop e mobile
      let isTouchDevice = false;
      
      // ===== DESKTOP: Mouse Events =====
      newInput.addEventListener('mousedown', (e) => {
        if (isTouchDevice) return; // Ignorar se for touch device
        this.handleLongPressStart(newInput, e);
      });
      
      newInput.addEventListener('mouseup', (e) => {
        if (isTouchDevice) return;
        this.handleLongPressEnd(newInput);
        
        // Se NÃO foi long-press, incrementar
        if (!this.wasLongPress) {
          this.handleClick(newInput);
        }
        this.wasLongPress = false;
      });
      
      newInput.addEventListener('mouseleave', () => {
        if (isTouchDevice) return;
        this.handleLongPressEnd(newInput);
        this.wasLongPress = false;
      });
      
      // ===== MOBILE: Touch Events =====
      let touchStartX = 0;
      let touchStartY = 0;
      let touchMoved = false;
      const TOUCH_MOVE_THRESHOLD = 10; // pixels para considerar movimento
      
      newInput.addEventListener('touchstart', (e) => {
        isTouchDevice = true;
        const touch = e.touches[0];
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchMoved = false;
        
        // Só prevenir default se não estiver em scroll
        if (e.target === newInput) {
          e.preventDefault();
        }
        
        this.handleLongPressStart(newInput, e);
      }, { passive: false });
      
      newInput.addEventListener('touchmove', (e) => {
        if (!touchMoved && e.touches.length > 0) {
          const touch = e.touches[0];
          const deltaX = Math.abs(touch.clientX - touchStartX);
          const deltaY = Math.abs(touch.clientY - touchStartY);
          
          if (deltaX > TOUCH_MOVE_THRESHOLD || deltaY > TOUCH_MOVE_THRESHOLD) {
            touchMoved = true;
            // Cancelar long-press se usuário estiver deslizando
            this.handleLongPressEnd(newInput);
          }
        }
      }, { passive: true });
      
      newInput.addEventListener('touchend', (e) => {
        // Só processar se foi um toque válido (não scroll)
        if (!touchMoved) {
          e.preventDefault(); // Prevenir click fantasma apenas se não foi scroll
        }
        
        this.handleLongPressEnd(newInput);
        
        // Se NÃO foi long-press e NÃO foi movimento, incrementar
        if (!this.wasLongPress && !touchMoved) {
          this.handleClick(newInput);
        }
        this.wasLongPress = false;
        touchMoved = false;
      }, { passive: false });
      
      newInput.addEventListener('touchcancel', (e) => {
        // Usuário deslizou para fora - cancelar long-press
        this.handleLongPressEnd(newInput);
        this.wasLongPress = false;
        touchMoved = false;
      });
      
      // Prevenir foco e edição direta
      newInput.addEventListener('focus', (e) => {
        e.preventDefault();
        newInput.blur();
      });
      
      // Prevenir teclado
      newInput.addEventListener('keydown', (e) => {
        e.preventDefault();
        return false;
      });
    });
  }

  handleClick(input) {
    if (input.disabled || input.classList.contains('out-of-stock')) return;
    
    const currentValue = parseInt(input.value) || 0;
    const maxStock = parseInt(input.dataset.maxStock) || Infinity;
    const newValue = Math.min(currentValue + 1, maxStock);
    
    input.value = newValue;
    
    // Atualizar cor (cinza se 0, normal se > 0)
    this.updateInputColor(input);
    
    // Verificar se atingiu o máximo
    this.checkStockLimit(input, newValue);
    
    // Atualizar carrinho em tempo real
    this.queueCartUpdate(input);
    
    // Animação visual de feedback
    input.classList.add('quantity-clicked');
    setTimeout(() => {
      input.classList.remove('quantity-clicked');
    }, 200);
  }
  
  updateInputColor(input) {
    const value = parseInt(input.value) || 0;
    if (value === 0) {
      input.classList.add('quantity-zero');
    } else {
      input.classList.remove('quantity-zero');
    }
  }

  handleLongPressStart(input, event) {
    if (input.disabled || input.classList.contains('out-of-stock')) return;
    
    this.wasLongPress = false;
    
    // Salvar valor original para feedback háptico
    const originalValue = parseInt(input.value) || 0;
    
    this.longPressTimer = setTimeout(() => {
      this.wasLongPress = true; // Marcar que foi long-press para não incrementar no click
      
      const newValue = 0;
      input.value = newValue;
      input.classList.remove('quantity-max-stock', 'quantity-error');
      this.removeMaxStockMessage(input);
      
      // Atualizar cor para cinza
      this.updateInputColor(input);
      
      // Feedback háptico quando quantidade é zerada (apenas se tinha valor antes e foi realmente zerado)
      if (originalValue > 0 && newValue === 0 && this.isTouchDevice) {
        // Padrão de vibração mais perceptível
        this.vibrateDevice([30, 50, 30]);
      }
      
      // Atualizar carrinho em tempo real
      this.queueCartUpdate(input);
      
      // Feedback visual
      input.classList.add('quantity-reset');
      setTimeout(() => {
        input.classList.remove('quantity-reset');
      }, 300);
    }, this.longPressDelay);
  }

  handleLongPressEnd(input) {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  async handleQuantityChange(input) {
    const variantId = input.dataset.variantId;
    const quantity = parseInt(input.value) || 0;
    
    if (quantity < 0) {
      input.value = 0;
      return;
    }
    
    // Verificar estoque máximo
    await this.checkStockLimit(input, quantity);
  }

  handleQuantityInput(input) {
    const quantity = parseInt(input.value) || 0;
    const maxStock = parseInt(input.dataset.maxStock) || Infinity;
    
    if (quantity > maxStock) {
      input.classList.add('quantity-error');
      this.showMaxStockMessage(input, maxStock);
    } else {
      input.classList.remove('quantity-error');
      this.removeMaxStockMessage(input);
    }
  }

  async checkStockLimit(input, quantity) {
    const variantId = input.dataset.variantId;
    if (!variantId) return;
    
    // Verificar estoque usando data attributes
    const maxStock = parseInt(input.dataset.maxStock) || 9999;
    const inventoryQty = parseInt(input.dataset.inventoryQuantity) || null;
    
    if (maxStock < 9999 && quantity >= maxStock) {
      input.classList.add('quantity-max-stock');
      this.showMaxStockMessage(input, maxStock);
      
      // Limitar valor ao máximo
      if (quantity > maxStock) {
        input.value = maxStock;
      }
    } else {
      input.classList.remove('quantity-max-stock');
      this.removeMaxStockMessage(input);
    }
    
    // Também verificar no carrinho se necessário
    try {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const cartItem = cart.items.find(item => item.variant_id.toString() === variantId.toString());
        
        if (cartItem && cartItem.inventory_quantity !== undefined) {
          const availableStock = cartItem.inventory_quantity;
          
          if (availableStock !== null && quantity >= availableStock) {
            input.classList.add('quantity-max-stock');
            this.showMaxStockMessage(input, availableStock);
            
            // Atualizar data attributes
            input.dataset.maxStock = availableStock;
            input.dataset.inventoryQuantity = availableStock;
            
            // Limitar valor ao máximo
            if (quantity > availableStock) {
              input.value = availableStock;
            }
          }
        }
      }
    } catch (error) {
      console.error('Erro ao verificar estoque no carrinho:', error);
    }
  }

  showMaxStockMessage(input, maxStock) {
    // Remover mensagem anterior se existir
    this.removeMaxStockMessage(input);
    
    // Criar ou atualizar mensagem tipo toast/popup
    let messageEl = input.parentElement.querySelector('.max-stock-message');
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.className = 'max-stock-message';
      messageEl.style.cssText = `
        position: absolute;
        top: -30px;
        left: 50%;
        transform: translateX(-50%);
        background: #e74c3c;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 0.7rem;
        white-space: nowrap;
        z-index: 1000;
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
      `;
      input.parentElement.appendChild(messageEl);
    }
    
    messageEl.textContent = 'MÁXIMO DISPONÍVEL';
    messageEl.style.display = 'block';
    messageEl.style.fontSize = '0.9rem'; // Aumentar tamanho
    messageEl.style.fontWeight = '700'; // Bold
    
    // Animar aparecimento
    setTimeout(() => {
      messageEl.style.opacity = '1';
    }, 10);
    
    // Auto-remover após 2 segundos
    setTimeout(() => {
      this.removeMaxStockMessage(input);
    }, 2000);
  }

  removeMaxStockMessage(input) {
    if (!input || !input.parentElement) {
      return;
    }
    const messageEl = input.parentElement.querySelector('.max-stock-message');
    if (messageEl) {
      messageEl.style.opacity = '0';
      setTimeout(() => {
        messageEl.style.display = 'none';
      }, 300);
    }
  }

  // ===== FILA DE ATUALIZAÇÃO DO CARRINHO =====
  // Envia no clique e serializa: nunca ha duas requisicoes em voo.
  // Sem isso, dois cliques rápidos disparavam dois /cart/add.js consecutivos
  // usando um this.cartData desatualizado: como add.js SOMA a quantidade,
  // o carrinho terminava com mais peças do que o grid da página mostrava.
  pendingCartUpdates = {};
  pendingCartInputs = {};
  cartFlushInFlight = false;

  queueCartUpdate(input) {
    const variantId = input.dataset.variantId;
    if (!variantId) {
      UB_DEBUG_GRID && console.log('❌ Sem variantId');
      return;
    }

    // O valor atual do input é sempre a fonte da verdade
    this.pendingCartUpdates[variantId] = parseInt(input.value) || 0;
    this.pendingCartInputs[variantId] = input;

    // Dispara IMEDIATAMENTE. Se já houver uma requisição em andamento, o
    // clique fica acumulado e sai numa única requisição assim que ela voltar
    // (coalescência) - sem atraso artificial e sem enfileirar uma chamada
    // por clique.
    this.flushCartUpdates();
  }

  async flushCartUpdates() {
    if (this.cartFlushInFlight) return;

    const updates = this.pendingCartUpdates;
    const inputs = this.pendingCartInputs;
    this.pendingCartUpdates = {};
    this.pendingCartInputs = {};

    if (Object.keys(updates).length === 0) return;

    this.cartFlushInFlight = true;
    try {
      await this.updateCartInRealTime(updates, inputs);
    } catch (error) {
      console.error('❌ Erro na fila de atualização do carrinho:', error);
    } finally {
      this.cartFlushInFlight = false;
    }

    // Clicou durante a requisição? Enviar agora o valor final acumulado.
    if (Object.keys(this.pendingCartUpdates).length > 0) {
      this.flushCartUpdates();
    }
  }

  async updateCartInRealTime(updates, inputs = {}) {
    UB_DEBUG_GRID && console.log('🔄 Atualizando carrinho:', updates);

    try {
      // /cart/update.js define a quantidade ABSOLUTA e devolve o carrinho
      // completo. Diferente de /cart/add.js ele não soma, então reenviar o
      // mesmo valor nunca duplica a quantidade.
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Erro na resposta:', errorData);

        // Se o erro for sobre quantidade máxima, mostrar mensagem no input
        if (errorData.description && errorData.description.includes('maximum quantity')) {
          Object.values(inputs).forEach(input => {
            input.classList.add('quantity-error', 'quantity-max-stock');
            this.showMaxStockMessage(input, parseInt(input.dataset.maxStock) || 0);
          });
        }

        // Realinhar o grid com o estado real para não exibir valor falso
        await this.loadCartData();
        this.syncQuantitiesFromCart();
        return;
      }

      const cartState = await response.json();
      UB_DEBUG_GRID && console.log('✅ Resposta da API recebida:', cartState);

      // Atualizar dados locais do carrinho
      this.cartData = cartState;

      // IMPORTANTE: Atualizar badge/ícone do carrinho no header (async)
      await this.updateCartIcon(cartState);

      // Disparar eventos de atualização
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartState }
      }));

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // CRÍTICO: Garantir que cart-drawer está pronto antes de publicar evento
        const isReady = await this.ensureCartDrawerReady();

        if (isReady) {
          // Publicar evento - o cart-drawer escutará via subscriber e renderizará automaticamente
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-grid-matrix-realtime',
            cartData: cartState
          });
          UB_DEBUG_GRID && console.log('📤 Evento cartUpdate publicado - cart-drawer será atualizado automaticamente');
        } else {
          // Se drawer não está pronto, forçar atualização direta
          console.warn('⚠️ Cart-drawer não está pronto, atualizando diretamente...');
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.renderMatrixGrid === 'function') {
            await cartDrawer.renderMatrixGrid(cartState);
          }
        }
      }

      // Realinhar o grid com o carrinho real (protege contra ajuste de estoque).
      // Só sincroniza se o usuário não tiver clicado de novo nesse meio tempo.
      if (Object.keys(this.pendingCartUpdates).length === 0) {
        this.syncQuantitiesFromCart();
      }

      // Verificar se atingiu estoque máximo
      for (const variantId of Object.keys(updates)) {
        const input = inputs[variantId];
        if (input) await this.checkStockLimit(input, updates[variantId]);
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar carrinho em tempo real:', error);
    }
  }
  subscribeToCartUpdates() {
    // Usar o sistema pub/sub do tema
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        UB_DEBUG_GRID && console.log('📥 Product Grid recebeu cartUpdate:', event.source);
        
        // CRÍTICO: Ignorar eventos do próprio product-grid-matrix para evitar loops
        // Mas processar eventos do cart-drawer e outras fontes
        if (event.source === 'product-grid-matrix-realtime' || event.source === 'product-grid-matrix') {
          UB_DEBUG_GRID && console.log('⏭️ Ignorando evento interno do product-grid-matrix');
          return;
        }
        
        // OTIMIZAÇÃO: Usar dados do evento ao invés de fazer fetch desnecessário
        if (event.cartData && event.cartData.items && Array.isArray(event.cartData.items)) {
          UB_DEBUG_GRID && console.log('✅ Usando cartData do evento (sem fetch)');
          // Garantir que cartData está completo e válido
          this.cartData = event.cartData;
          // Sincronizar imediatamente com os dados recebidos
          this.syncQuantitiesFromCart();
        } else {
          UB_DEBUG_GRID && console.log('⚠️ Evento sem cartData válido, buscando carrinho atualizado...');
          // Buscar carrinho atualizado quando evento não tem dados completos
          await this.loadCartData();
          this.syncQuantitiesFromCart();
        }
      });
    }
  }

  async updateCartIcon(cartState) {
    if (!cartState) return;
    
    // CRÍTICO: Calcular item_count REAL para evitar inconsistências
    // Às vezes cartState.item_count é 0 mas cartState.items tem itens
    const actualItemCount = cartState && cartState.items && Array.isArray(cartState.items)
      ? cartState.items.reduce((total, item) => total + (item.quantity || 0), 0)
      : (cartState.item_count || 0);
    
    // Corrigir cartState.item_count se estiver inconsistente
    if (actualItemCount !== (cartState.item_count || 0)) {
      console.warn('⚠️ updateCartIcon: Corrigindo item_count de', cartState.item_count, 'para', actualItemCount);
      cartState.item_count = actualItemCount;
    }
    
    UB_DEBUG_GRID && console.log('🛒 Item count do carrinho (calculado):', actualItemCount);
    
    // Atualizar ícone do carrinho no header - usar função helper com retry
    await this.updateCartBadge(actualItemCount);
    
    // IMPORTANTE: Atualizar também os totais no cart-drawer se ele existir
    // Isso garante sincronização mesmo quando o drawer não foi aberto ainda
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.updateTotalsAndBadge === 'function') {
      await cartDrawer.updateTotalsAndBadge(cartState);
    }
  }
  
  // Função para atualizar apenas o badge do carrinho (pode ser chamada assincronamente)
  async updateCartBadge(itemCount) {
    // Garantir que badge existe (com retry logic e criação dinâmica se necessário)
    const badgeElements = await this.ensureCartBadgeExists();
    
    if (!badgeElements) {
      // Se ainda não conseguiu, tentar novamente após um delay
      setTimeout(() => this.updateCartBadge(itemCount), 200);
      return;
    }
    
    const { cartIconBubble, bubble, countSpan } = badgeElements;
    
    // Atualizar contador
    if (countSpan) {
      countSpan.textContent = itemCount;
      UB_DEBUG_GRID && console.log('✅ Badge do carrinho atualizado (product-grid) para:', itemCount);
    }
    
    // Garantir que o bubble está visível se houver itens
    if (bubble) {
      if (itemCount > 0) {
        cartIconBubble.classList.add('cart-count-bubble--visible');
        bubble.style.display = '';
      } else {
        cartIconBubble.classList.remove('cart-count-bubble--visible');
        bubble.style.display = 'none';
      }
    }
    
    // Atualizar aria-label
    cartIconBubble.setAttribute('aria-label', `Carrinho com ${itemCount} item${itemCount !== 1 ? 's' : ''}`);
  }

  setupRemoveAllButton() {
    const removeAllBtn = document.querySelector('.product-grid-remove-all');
    if (!removeAllBtn) return;
    
    removeAllBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      
      // Confirmar ação
      if (!confirm('Deseja remover todas as quantidades do produto?')) {
        return;
      }
      
      // Garantir que cartData está atualizado
      if (!this.cartData) {
        await this.loadCartData();
      }
      
      // Encontrar todos os inputs da grade
      const gridInputs = document.querySelectorAll('.quantity-grid-input');
      
      // Coletar todos os line item IDs que estão no carrinho e precisam ser zerados
      const updates = {};
      const inputsToUpdate = [];
      
      for (const input of gridInputs) {
        const currentValue = parseInt(input.value) || 0;
        if (currentValue > 0) {
          const variantId = input.dataset.variantId;
          if (!variantId) continue;
          
          // Buscar item no carrinho para obter line item id
          const cartItem = this.cartData && this.cartData.items ? 
            this.cartData.items.find(item => item.variant_id.toString() === variantId.toString()) : null;
          
          if (cartItem && cartItem.id) {
            // Item está no carrinho - adicionar ao objeto de updates
            updates[cartItem.id.toString()] = 0;
            inputsToUpdate.push(input);
          } else {
            // Item não está no carrinho - apenas atualizar UI localmente
            inputsToUpdate.push(input);
          }
        }
      }
      
      // Atualizar UI imediatamente (zerar todos os inputs)
      inputsToUpdate.forEach(input => {
        input.value = 0;
        this.updateInputColor(input);
        input.classList.remove('quantity-max-stock', 'quantity-error');
        this.removeMaxStockMessage(input);
      });
      
      // Se há itens no carrinho para remover, fazer uma única chamada à API
      if (Object.keys(updates).length > 0) {
        try {
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates })
          });
          
          if (response.ok) {
            const cartState = await response.json();
            this.cartData = cartState;
            
            // Atualizar badge do carrinho
            await this.updateCartIcon(cartState);
            
            // Disparar evento de atualização
            document.dispatchEvent(new CustomEvent('cart:updated', {
              bubbles: true,
              detail: { cart: cartState }
            }));
            
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { 
                source: 'product-grid-matrix-remove-all', 
                cartData: cartState 
              });
            }
          } else {
            console.error('❌ Erro ao remover itens do carrinho');
            // Em caso de erro, recarregar dados do carrinho para sincronizar
            await this.loadCartData();
            this.syncQuantitiesFromCart();
          }
        } catch (error) {
          console.error('❌ Erro ao remover itens do carrinho:', error);
          // Em caso de erro, recarregar dados do carrinho para sincronizar
          await this.loadCartData();
          this.syncQuantitiesFromCart();
        }
      } else {
        // Nenhum item estava no carrinho, apenas sincronizar
        await this.loadCartData();
        this.syncQuantitiesFromCart();
      }
      
      // Feedback visual
      removeAllBtn.style.color = '#4caf50';
      setTimeout(() => {
        removeAllBtn.style.color = '';
      }, 1000);
    });
    
    // Adicionar hover effect via JS
    removeAllBtn.addEventListener('mouseenter', () => {
      removeAllBtn.style.backgroundColor = '#ffebee';
      removeAllBtn.style.color = '#e74c3c';
    });
    
    removeAllBtn.addEventListener('mouseleave', () => {
      removeAllBtn.style.backgroundColor = '';
      removeAllBtn.style.color = '#999';
    });
  }

  disconnect() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
    }
  }
}

// Inicializar quando o DOM estiver pronto
let productGridMatrixInstance = null;

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.quantity-grid-input')) {
    productGridMatrixInstance = new ProductGridMatrix();
  }
});

// Reinicializar quando o conteúdo do produto mudar (ex: mudança de variante)
if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      // Dar tempo para o DOM atualizar
      setTimeout(() => {
        if (!productGridMatrixInstance && document.querySelector('.quantity-grid-input')) {
          productGridMatrixInstance = new ProductGridMatrix();
        } else if (productGridMatrixInstance) {
          productGridMatrixInstance.setupEventListeners();
        }
      }, 100);
    });
  });
}

